import type { BuildingCategory, BuildingInStoreInfo, FloorInfo } from '@/services/dto';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useConfigStore } from './configStore';
import type { TFunction } from 'i18next';
import type { FacilityActions, FacilityFilterConfig, FacilityState } from '@/stores/types/facilityStore';
import facilityUtils, { getFacilityCategories, getFacilityListCategories, getFacilityMenu, resolveFacilityPoiContents } from '@/utils/facility';
import { isDutyFreePoi } from '@/utils/shop';
import { FACILITY_SEARCH_MODE } from '@/enums/facility';
import type { PoiContentSlide } from '@/types/shop';
import { STORE_CODE_MAP_JAMSIL } from '@/constants/app';

const optionAllCategory: BuildingCategory = {
    clientCode: 'all',
    name: {
        ko: 'common.text.all_category',
        en: 'common.text.all_category',
        ja: 'common.text.all_category',
        'zh-CN': 'common.text.all_category',
        'zh-TW': 'common.text.all_category',
    },
    id: '',
    parentClientCode: '',
    indentLevel: 0,
    categoryCode: '',
    parentCategoryCode: '',
};

const resolveDefaultBuildingId = (
    buildings: BuildingInStoreInfo[] | undefined,
    buildingMenus: Record<string, any> | null | undefined,
    language: string,
    mainLanguage?: string,
    storeCode?: string | null
): string => {
    if (!buildings?.length) return 'all';
    if (buildings.length > 1) return 'all';

    if (storeCode === STORE_CODE_MAP_JAMSIL) return 'all';

    const facilityMenu = getFacilityMenu(buildingMenus, language, mainLanguage);
    const pois = facilityMenu?.poiContents ?? [];
    if (pois.some((poi) => isDutyFreePoi(poi, language))) return 'all';

    return buildings[0].id;
};

interface FacilityStore extends FacilityState, FacilityActions {}

export const useFacilityStore = create<FacilityStore>()(
    devtools(
        (set, get) => ({
            rawFacilityContents: [],
            facilityContents: [],
            selectedFloor: {
                id: 'all',
                label: '',
                value: '',
                data: null,
            },
            selectedBuildingId: 'all',
            selectedCategory: null,
            t: null,
            currentMode: FACILITY_SEARCH_MODE.SEARCH_BY_FLOOR,
            keyboardLanguage: 'ko',

            setKeyboardLanguage: (language: 'ko' | 'en') => {
                const prev = get().keyboardLanguage;
                if (prev === language) return;

                set({ keyboardLanguage: language });
            },

            setCurrentMode: (mode: FACILITY_SEARCH_MODE) => {
                const prev = get().currentMode;
                if (prev === mode) return;

                set({ currentMode: mode });
            },

            setSelectedFloor: (floor: { id: string; label: string; value: string; data: FloorInfo | string[] | null } | null) => {
                const prev = get().selectedFloor;
                if (prev?.id === floor?.id) return;

                set({ selectedFloor: floor });
            },

            setSelectedBuildingId: (buildingId: string | null) => {
                const prev = get().selectedBuildingId;
                if (prev === buildingId) return;

                set({ selectedBuildingId: buildingId });
            },

            setSelectedCategory: (category: BuildingCategory) => {
                const prev = get().selectedCategory;
                if (prev?.clientCode === category?.clientCode) return;

                set({ selectedCategory: category });
            },

            setTranslationFunction: (t: TFunction<'translation', undefined>) => {
                set({ t });
            },

            resetFilters: () => {
                const state = get();
                const { t } = state;
                const { config } = useConfigStore.getState();
                const { server, ui } = config;

                const allFloorText = t ? t('facility.search.text.content.all_floor') : 'All Floors';
                const newBuildingId = resolveDefaultBuildingId(
                    server?.store?.buildings,
                    server?.buildingMenus,
                    ui.language,
                    ui.mainLanguage,
                    server?.store?.storeCode
                );

                set({
                    selectedFloor: {
                        id: 'all',
                        label: allFloorText,
                        value: 'all',
                        data: null,
                    },
                    selectedBuildingId: newBuildingId,
                    selectedCategory: optionAllCategory,
                });
            },

            updateFacilityContents: (searchMode: FACILITY_SEARCH_MODE): PoiContentSlide[] => {
                const state = get();
                const { selectedFloor, selectedBuildingId, selectedCategory } = state;
                const { config } = useConfigStore.getState();
                const {
                    ui: { language, mainLanguage },
                } = config;

                const allBuildings = config.server?.store?.buildings ?? [];
                if (!allBuildings.length) {
                    if (state.facilityContents?.length > 0) {
                        set({ facilityContents: [] });
                    }
                    return [];
                }

                const facilityCategories = getFacilityListCategories(config.server?.buildingMenus, language, mainLanguage);
                const facilityMenu = getFacilityMenu(config.server?.buildingMenus, language, mainLanguage);
                const pois = resolveFacilityPoiContents(facilityMenu, selectedBuildingId, searchMode);
                const storeCode = config.server?.store?.storeCode ?? null;
                const rawFacilityContents = facilityUtils.convertPoiContentToSlide(
                    language,
                    allBuildings,
                    pois.length ? pois : undefined,
                    facilityCategories,
                    storeCode
                );

                const facilityMenuForFilter = getFacilityMenu(config.server?.buildingMenus, language, mainLanguage);
                const menuCategoryForFilter = facilityMenuForFilter?.selectedCategories?.find((c) => c.clientCode === selectedCategory?.clientCode);
                const categoryCodesForFilter: string[] = menuCategoryForFilter?.mergeCategoryCode ?? [];

                const filterConfig: FacilityFilterConfig = {
                    language,
                    selectedBuildingId,
                    selectedFloor,
                    selectedCategory,
                    buildings: config.server?.store?.buildings,
                    categoryCodesForFilter,
                };

                if (!rawFacilityContents?.length) {
                    if (state.facilityContents?.length > 0) {
                        set({ facilityContents: [] });
                    }
                    return [];
                }

                const facilityFilter = facilityUtils.createFacilityFilter(searchMode, filterConfig);
                const filteredContents = rawFacilityContents.filter(facilityFilter);

                if (filteredContents?.length === 0) {
                    if (state.facilityContents?.length > 0) {
                        set({ facilityContents: [] });
                    }
                    return [];
                }

                const getPriority = (char: string) => {
                    if (!char) return 4;
                    if (/[ㄱ-ㅎㅏ-ㅣ가-힣]/.test(char)) return 1;
                    if (/[a-zA-Z]/.test(char)) return 2;
                    if (/[0-9]/.test(char)) return 3;
                    return 4;
                };

                const sortedContents = filteredContents.sort((a, b) => {
                    const nameA = (a?.data?.name?.[language] || '').trim();
                    const nameB = (b?.data?.name?.[language] || '').trim();

                    const priorityA = getPriority(nameA.charAt(0));
                    const priorityB = getPriority(nameB.charAt(0));

                    if (priorityA !== priorityB) {
                        return priorityA - priorityB;
                    }

                    return nameA.localeCompare(nameB, language, {
                        numeric: true,
                        sensitivity: 'base',
                        ignorePunctuation: true,
                    });
                });

                set({ facilityContents: sortedContents });

                return sortedContents;
            },

            initialize: (t: TFunction<'translation', undefined>) => {
                const state = get();
                if (state.t === t && state.rawFacilityContents?.length > 0) {
                    return;
                }

                const { config } = useConfigStore.getState();
                const {
                    ui: { language, mainLanguage },
                    server,
                } = config;

                const allBuildings = server?.store?.buildings ?? [];
                const storeCode = server?.store?.storeCode ?? null;
                const selectedBuildingId = resolveDefaultBuildingId(allBuildings, server?.buildingMenus, language, mainLanguage, storeCode);
                const facilityCategories = getFacilityCategories(server?.buildingMenus, language, mainLanguage);
                const facilityMenu = getFacilityMenu(server?.buildingMenus, language, mainLanguage);
                const pois = resolveFacilityPoiContents(facilityMenu, selectedBuildingId, FACILITY_SEARCH_MODE.SEARCH_BY_FLOOR);
                const rawFacilityContents = facilityUtils.convertPoiContentToSlide(
                    language,
                    allBuildings,
                    pois.length ? pois : undefined,
                    facilityCategories,
                    storeCode
                );

                set({
                    t,
                    rawFacilityContents,
                    currentMode: FACILITY_SEARCH_MODE.SEARCH_BY_FLOOR,
                    selectedBuildingId,
                    selectedCategory: optionAllCategory,
                });
            },
        }),
        {
            name: 'facility-store',
        }
    )
);

let previousBuildings: BuildingInStoreInfo[] | undefined = undefined;
let previousLanguage: string = '';
let previousBuildingMenus: Record<string, unknown> | undefined = undefined;

useConfigStore.subscribe(() => {
    const { config } = useConfigStore.getState();
    const currentBuildings = config.server?.store?.buildings;
    const currentLanguage = config.ui?.language;
    const currentBuildingMenus = config.server?.buildingMenus as Record<string, unknown> | undefined;

    if (currentBuildings !== previousBuildings || currentLanguage !== previousLanguage || currentBuildingMenus !== previousBuildingMenus) {
        previousBuildings = currentBuildings;
        previousLanguage = currentLanguage;
        previousBuildingMenus = currentBuildingMenus;

        const { initialize, t } = useFacilityStore.getState();
        if (t) {
            initialize(t);
        }
    }
});
